import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import LinearProgress from "@mui/material/LinearProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import PieChart from "examples/Charts/PieChart";
import {
  GetActivityCountryPurchasePotentialCall,
  GetActivityFeatureUsageCall,
  GetActivityInAppPurchaseCall,
  GetActivityOverviewCall,
  GetActivityTimelineCall,
} from "../../redux/api/activity.api";
import {
  formatScreenChartLabel,
  getPushTypeHumanLabel,
  parseEventMetadata,
} from "./screenRouteCatalog";

const toNumber = (value) => Number(value || 0);

const toChartData = (rows = [], labelKey, valueKey, label = "Count", limit = 8) => {
  const list = Array.isArray(rows) ? rows.slice(0, limit) : [];
  return {
    labels: list.map((x) => String(x?.[labelKey] || "Unknown")),
    datasets: {
      label,
      data: list.map((x) => toNumber(x?.[valueKey])),
    },
  };
};

const getActionCount = (actions = [], key) =>
  toNumber(actions.find((a) => a.action_name === key)?.count);

function ActivityAnalyticsPage() {
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [iap, setIap] = useState(null);
  const [feature, setFeature] = useState(null);
  const [country, setCountry] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelinePage, setTimelinePage] = useState(0);
  const [timelineRowsPerPage, setTimelineRowsPerPage] = useState(25);
  const [countryPage, setCountryPage] = useState(0);
  const [countryRowsPerPage, setCountryRowsPerPage] = useState(8);
  const [slidesPage, setSlidesPage] = useState(0);
  const [slidesRowsPerPage, setSlidesRowsPerPage] = useState(10);

  const filter = useMemo(() => {
    const to = new Date();
    const from = new Date();
    if (range === "24h") from.setHours(from.getHours() - 24);
    if (range === "7d") from.setDate(from.getDate() - 7);
    if (range === "30d") from.setDate(from.getDate() - 30);
    if (range === "90d") from.setDate(from.getDate() - 90);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [range]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      GetActivityOverviewCall(filter),
      GetActivityInAppPurchaseCall(filter),
      GetActivityFeatureUsageCall(filter),
      GetActivityCountryPurchasePotentialCall(filter),
      GetActivityTimelineCall({ ...filter, page: timelinePage + 1, limit: timelineRowsPerPage }),
    ])
      .then(([overviewRes, iapRes, featureRes, countryRes, timelineRes]) => {
        if (!mounted) return;
        setOverview(overviewRes || null);
        setIap(iapRes || null);
        setFeature(featureRes || null);
        setCountry(countryRes || null);
        setTimeline(timelineRes || null);
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [filter, timelinePage, timelineRowsPerPage]);

  useEffect(() => {
    setTimelinePage(0);
    setCountryPage(0);
    setSlidesPage(0);
  }, [range]);

  const topScreensChart = toChartData(
    (overview?.top_screens || []).map((row) => ({
      ...row,
      chart_label: formatScreenChartLabel(row.screen_name),
    })),
    "chart_label",
    "count",
    "Views",
    10,
  );
  const topActionsChart = toChartData(overview?.top_actions, "action_name", "count", "Actions");
  const iapFunnelChart = toChartData(iap?.actions, "action_name", "count", "In-app purchase");
  const featureUsageChart = toChartData(
    (feature?.screen_usage || []).map((row) => ({
      ...row,
      chart_label: formatScreenChartLabel(row.screen_name),
    })),
    "chart_label",
    "count",
    "Usage",
    10,
  );
  const pushOpensTotal = toNumber(overview?.push_opens_total);
  const pushOpensUniqueUsers = toNumber(overview?.push_opens_unique_users);
  const pushOpensByType = Array.isArray(overview?.push_opens_by_type) ? overview.push_opens_by_type : [];
  const pushTypesCount = pushOpensByType.length;

  const pushPie = {
    labels: pushOpensByType.slice(0, 10).map((row) => getPushTypeHumanLabel(row.notification_type)),
    datasets: {
      label: "Push opens",
      data: pushOpensByType.slice(0, 10).map((row) => toNumber(row.count)),
      backgroundColors: ["info", "success", "warning", "error", "dark", "primary", "secondary", "info", "success", "warning"],
    },
  };

  const pushBarChart = toChartData(
    pushOpensByType.map((row) => ({
      ...row,
      chart_label: `${getPushTypeHumanLabel(row.notification_type)} [${row.notification_type}]`,
    })),
    "chart_label",
    "count",
    "Opens",
    12,
  );

  const topSlides = Array.isArray(overview?.top_slides) ? overview.top_slides : [];
  const slidesStart = slidesPage * slidesRowsPerPage;
  const pagedTopSlides = topSlides.slice(slidesStart, slidesStart + slidesRowsPerPage);
  const topSlidesTotalOpens = topSlides.reduce((s, r) => s + toNumber(r.opens), 0);
  const topSlidesChart = toChartData(
    topSlides.slice(0, 15).map((row) => {
      const labelBase = String(row.slide_title || row.slide_id || "—");
      const chart_label =
        labelBase.length > 44 ? `${labelBase.slice(0, 42)}…` : labelBase;
      return { ...row, chart_label };
    }),
    "chart_label",
    "opens",
    "Slide opens",
    15,
  );

  const countries = Array.isArray(country?.countries) ? country.countries : [];
  const countryStart = countryPage * countryRowsPerPage;
  const pagedCountries = countries.slice(countryStart, countryStart + countryRowsPerPage);
  const countryPie = {
    labels: countries.slice(0, 8).map((c) => c.country || "Unknown"),
    datasets: {
      label: "Paywall views",
      data: countries.slice(0, 8).map((c) => toNumber(c.paywall_views)),
      backgroundColors: ["info", "success", "warning", "error", "dark", "primary", "secondary", "info"],
    },
  };

  const paywallViews = getActionCount(iap?.actions || [], "paywall_viewed");
  const checkoutStarts = getActionCount(iap?.actions || [], "checkout_tap");
  const checkoutSuccess = getActionCount(iap?.actions || [], "checkout_success");
  const checkoutCancel = getActionCount(iap?.actions || [], "checkout_cancelled");
  const funnelStep1 = paywallViews ? 100 : 0;
  const funnelStep2 = paywallViews ? Math.round((checkoutStarts / paywallViews) * 100) : 0;
  const funnelStep3 = checkoutStarts ? Math.round((checkoutSuccess / checkoutStarts) * 100) : 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              App Analytics
            </MDTypography>
            <MDTypography variant="button" color="text">
              Modern overview of behavior, monetization funnel, and content demand
            </MDTypography>
          </MDBox>
          <TextField
            select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            size="small"
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="24h">Last 24h</MenuItem>
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
          </TextField>
        </MDBox>

        {loading && <LinearProgress color="info" sx={{ mb: 2 }} />}

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              color="info"
              icon="group"
              title="Active users"
              count={toNumber(overview?.active_users)}
              percentage={{ color: "success", label: "unique users in selected range" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              color="success"
              icon="ads_click"
              title="Paywall reached"
              count={paywallViews}
              percentage={{ color: "success", label: "users entering purchase screen" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              color="warning"
              icon="shopping_cart_checkout"
              title="Checkout started"
              count={checkoutStarts}
              percentage={{ color: "warning", label: "intent to buy" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              color="error"
              icon="paid"
              title="Checkout success"
              count={checkoutSuccess}
              percentage={{ color: "success", label: `${checkoutCancel} cancellations` }}
            />
          </Grid>
        </Grid>

        <MDBox mt={4}>
          <MDTypography variant="h5" fontWeight="bold" gutterBottom>
            Push notifications (FCM)
          </MDTypography>
          <MDTypography variant="button" color="text" display="block" mb={2}>
            Opens are counted when a logged-in user taps a notification and the app handles the payload (`push_click`
            events). Each row is the `type` field from your FCM data payload.
          </MDTypography>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={12} sm={6} md={4}>
              <ComplexStatisticsCard
                color="primary"
                icon="notifications_active"
                title="Total push opens"
                count={pushOpensTotal}
                percentage={{ color: "info", label: "tap-throughs in this date range" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <ComplexStatisticsCard
                color="info"
                icon="person"
                title="Users who opened a push"
                count={pushOpensUniqueUsers}
                percentage={{ color: "success", label: "distinct accounts (any notification type)" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <ComplexStatisticsCard
                color="secondary"
                icon="category"
                title="Notification types used"
                count={pushTypesCount}
                percentage={{ color: "text", label: "distinct `notification_type` values" }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              {pushOpensByType.length === 0 ? (
                <Card sx={{ height: "100%", minHeight: "20rem" }}>
                  <MDBox p={3} display="flex" alignItems="center" justifyContent="center" height="100%">
                    <MDTypography variant="button" color="text" textAlign="center">
                      No push open data for this period — pie chart will appear once users open notifications from the
                      app (logged in).
                    </MDTypography>
                  </MDBox>
                </Card>
              ) : (
                <PieChart
                  icon={{ color: "primary", component: "notifications" }}
                  title="Share of opens by type"
                  description={`${pushOpensTotal} total opens · top ${Math.min(10, pushOpensByType.length)} types`}
                  chart={pushPie}
                  height="20rem"
                />
              )}
            </Grid>
            <Grid item xs={12} md={7}>
              <Card sx={{ height: "100%" }}>
                <MDBox p={2}>
                  <MDTypography variant="h6">Opens by notification type</MDTypography>
                  <MDTypography variant="button" color="text" display="block" mb={1}>
                    Human label, API `type`, open count, and unique users per type
                  </MDTypography>
                  <Divider sx={{ mb: 1 }} />
                  <MDBox sx={{ overflowX: "auto", maxHeight: { xs: 280, md: 320 } }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Label</TableCell>
                          <TableCell>Payload type</TableCell>
                          <TableCell align="right">Opens</TableCell>
                          <TableCell align="right">Users</TableCell>
                          <TableCell align="right">% of opens</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pushOpensByType.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <MDTypography variant="caption" color="text">
                                No push opens in this range. Events require app version that logs `push_click` and users
                                must be logged in when opening from a notification.
                              </MDTypography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          pushOpensByType.map((row) => {
                            const opens = toNumber(row.count);
                            const users = toNumber(row.users);
                            const pct =
                              pushOpensTotal > 0 ? ((opens / pushOpensTotal) * 100).toFixed(1) : "0.0";
                            const rawType = row.notification_type ?? "—";
                            return (
                              <TableRow key={String(rawType)}>
                                <TableCell>{getPushTypeHumanLabel(rawType)}</TableCell>
                                <TableCell>
                                  <Chip label={String(rawType)} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell align="right">{opens}</TableCell>
                                <TableCell align="right">{users}</TableCell>
                                <TableCell align="right">{pct}%</TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            {pushOpensByType.length > 0 ? (
              <Grid item xs={12}>
                <ReportsBarChart
                  color="secondary"
                  title="Push opens by type (bar)"
                  description="Same data as the table — useful when comparing volumes"
                  date="live data"
                  chart={pushBarChart}
                />
              </Grid>
            ) : null}
          </Grid>
        </MDBox>

        <MDBox mt={4}>
          <MDTypography variant="h5" fontWeight="bold" gutterBottom>
            Popular slides (Tile viewer)
          </MDTypography>
          <MDTypography variant="button" color="text" display="block" mb={2}>
            Ranked by how often logged-in users open a slide in the full-screen viewer (`slide_view` events). Titles come
            from the most recent event per slide in this date range. Requires a recent mobile app build that sends these
            events.
          </MDTypography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <ComplexStatisticsCard
                color="dark"
                icon="visibility"
                title="Slide opens tracked"
                count={topSlidesTotalOpens}
                percentage={{
                  color: "text",
                  label: `${topSlides.length} distinct slides in top list`,
                }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              {topSlides.length === 0 ? (
                <Card sx={{ minHeight: 120 }}>
                  <MDBox p={3}>
                    <MDTypography variant="button" color="text">
                      No slide popularity data in this period yet. Open slides from the app (histology / embryology
                      viewer) after updating the app.
                    </MDTypography>
                  </MDBox>
                </Card>
              ) : (
                <ReportsBarChart
                  color="dark"
                  title="Most opened slides (top 15)"
                  description="Each bar is one slide (tissue); label shows title when available"
                  date="live data"
                  chart={topSlidesChart}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">All ranked slides (up to 50)</MDTypography>
                  <MDTypography variant="button" color="text" display="block" mb={1}>
                    Slide ID is the subtitle / tissue id used in the API. Opens = viewer sessions; Users = distinct
                    accounts.
                  </MDTypography>
                  <Divider sx={{ mb: 1 }} />
                  <MDBox sx={{ overflowX: "auto" }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Title (latest)</TableCell>
                          <TableCell>Slide ID</TableCell>
                          <TableCell>Catalog</TableCell>
                          <TableCell align="right">Opens</TableCell>
                          <TableCell align="right">Users</TableCell>
                          <TableCell align="right">Title ID</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedTopSlides.map((row) => (
                          <TableRow key={String(row.slide_id)}>
                            <TableCell>{row.slide_title || "—"}</TableCell>
                            <TableCell>
                              <Chip size="small" label={String(row.slide_id)} variant="outlined" />
                            </TableCell>
                            <TableCell>{row.catalog_type || "—"}</TableCell>
                            <TableCell align="right">{toNumber(row.opens)}</TableCell>
                            <TableCell align="right">{toNumber(row.users)}</TableCell>
                            <TableCell>
                              {row.title_id ? (
                                <Chip size="small" label={String(row.title_id)} variant="outlined" />
                              ) : (
                                "—"
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </MDBox>
                  <TablePagination
                    component="div"
                    count={topSlides.length}
                    page={slidesPage}
                    onPageChange={(_, newPage) => setSlidesPage(newPage)}
                    rowsPerPage={slidesRowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setSlidesRowsPerPage(parseInt(event.target.value, 10));
                      setSlidesPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={2}>
          <MDTypography variant="caption" color="text">
            Screen charts use human-readable labels; raw route names appear in [brackets]. Timeline rows include
            descriptions from the app.
          </MDTypography>
        </MDBox>

        <MDBox mt={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ReportsBarChart
                color="info"
                title="Top Screens"
                description="Where users navigate most (label + route name)"
                date="live data"
                chart={topScreensChart}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReportsLineChart
                color="success"
                title="Top Actions"
                description="Most frequent user interactions"
                date="live data"
                chart={topActionsChart}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReportsBarChart
                color="warning"
                title="Feature Usage"
                description="Screen views by feature area (label + route name)"
                date="live data"
                chart={featureUsageChart}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReportsLineChart
                color="dark"
                title="In-App Purchase Events"
                description="Funnel signals from paywall to conversion"
                date="live data"
                chart={iapFunnelChart}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <PieChart
                icon={{ color: "info", component: "public" }}
                title="Country Purchase Potential"
                description="Top countries by paywall traffic"
                chart={countryPie}
                height="18rem"
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">Purchase Funnel Health</MDTypography>
                  <MDTypography variant="button" color="text">
                    Quick conversion view from reach to purchase
                  </MDTypography>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={2}>
                    <MDBox>
                      <MDTypography variant="caption" color="text">
                        1) Paywall views ({paywallViews})
                      </MDTypography>
                      <LinearProgress variant="determinate" value={funnelStep1} color="info" />
                    </MDBox>
                    <MDBox>
                      <MDTypography variant="caption" color="text">
                        2) Checkout taps ({checkoutStarts}) - {funnelStep2}% of paywall traffic
                      </MDTypography>
                      <LinearProgress variant="determinate" value={funnelStep2} color="warning" />
                    </MDBox>
                    <MDBox>
                      <MDTypography variant="caption" color="text">
                        3) Purchases ({checkoutSuccess}) - {funnelStep3}% of checkout starts
                      </MDTypography>
                      <LinearProgress variant="determinate" value={funnelStep3} color="success" />
                    </MDBox>
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <MDTypography variant="button" fontWeight="medium">
                    Country conversion details
                  </MDTypography>
                  <MDBox sx={{ maxHeight: 220, overflowY: "auto", mt: 1 }}>
                    {pagedCountries.map((row) => (
                      <MDBox
                        key={row.country}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        py={1}
                        borderBottom="1px solid rgba(0,0,0,0.08)"
                      >
                        <MDTypography variant="caption" fontWeight="medium">
                          {row.country}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          views {toNumber(row.paywall_views)} | checkout {toNumber(row.checkout_taps)} | buy {toNumber(row.purchases)}
                        </MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>
                  <TablePagination
                    component="div"
                    count={countries.length}
                    page={countryPage}
                    onPageChange={(_, newPage) => setCountryPage(newPage)}
                    rowsPerPage={countryRowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setCountryRowsPerPage(parseInt(event.target.value, 10));
                      setCountryPage(0);
                    }}
                    rowsPerPageOptions={[5, 8, 10, 20]}
                  />
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={4}>
          <Card>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6">Recent Activity Timeline</MDTypography>
                <Chip
                  size="small"
                  label={`${Array.isArray(timeline?.data) ? timeline.data.length : 0} events`}
                  color="info"
                  variant="outlined"
                />
              </MDBox>
              <Divider sx={{ my: 2 }} />
              <MDBox sx={{ maxHeight: 360, overflowY: "auto" }}>
                {(timeline?.data || []).map((row) => {
                  const meta = parseEventMetadata(row);
                  const titleLine =
                    (row.event_type === "slide_view" && (meta.slide_title || row.element_name)) ||
                    meta.screen_title ||
                    (row.event_type === "push_click" && meta.notification_type_label) ||
                    row.screen_name ||
                    "-";
                  const subParts = [
                    row.event_type,
                    row.screen_name && row.screen_name !== titleLine ? `route: ${row.screen_name}` : null,
                    row.action_name,
                    row.element_name && row.event_type === "slide_view" ? `slide: ${row.element_name}` : null,
                    row.notification_type ? `push_type: ${row.notification_type}` : null,
                  ].filter(Boolean);
                  return (
                    <MDBox
                      key={row.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      py={1}
                      borderBottom="1px solid rgba(0,0,0,0.08)"
                    >
                      <MDBox maxWidth="75%">
                        <MDTypography variant="button" fontWeight="medium">
                          {titleLine}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          {subParts.join(" · ")}
                        </MDTypography>
                        {(meta.screen_description ||
                          meta.push_open_summary ||
                          (row.event_type === "slide_view" &&
                            (meta.catalog_type || meta.title_id))) && (
                          <MDTypography variant="caption" color="secondary" display="block" sx={{ mt: 0.5 }}>
                            {meta.push_open_summary ||
                              meta.screen_description ||
                              (row.event_type === "slide_view"
                                ? [meta.catalog_type && `catalog: ${meta.catalog_type}`, meta.title_id && `title_id: ${meta.title_id}`]
                                    .filter(Boolean)
                                    .join(" · ")
                                : "")}
                          </MDTypography>
                        )}
                        <MDTypography variant="caption" color="text" display="block" sx={{ mt: 0.25 }}>
                          {row.operation_status || "n/a"} | duration: {toNumber(row.duration_ms)} ms
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="caption" color="text" sx={{ flexShrink: 0, ml: 1 }}>
                        {row.occurred_at ? new Date(row.occurred_at).toLocaleString() : "-"}
                      </MDTypography>
                    </MDBox>
                  );
                })}
              </MDBox>
              <TablePagination
                component="div"
                count={Number(timeline?.total ?? -1)}
                page={timelinePage}
                onPageChange={(_, newPage) => setTimelinePage(newPage)}
                rowsPerPage={timelineRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setTimelineRowsPerPage(parseInt(event.target.value, 10));
                  setTimelinePage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ActivityAnalyticsPage;

